import { type VideoPlayer, type VideoSource, createVideoPlayer } from 'expo-video';
import { Platform } from 'react-native';

import { getEvergreenVideoSource } from './evergreen-video-source';
import { getCachedHeroVideoUri } from './hero-video-cache';

// The bundled evergreen video ships inside each brand app's binary so it plays
// offline / on slow networks
export { registerEvergreenVideoSource } from './evergreen-video-source';

export const EVERGREEN_VIDEO_KEY = 'evergreen';

export function resolveHeroVideoSource(key: string): VideoSource {
  if (key === EVERGREEN_VIDEO_KEY) {
    return getEvergreenVideoSource();
  }
  // Prefer our on-disk cache when present — instant paint from a local file,
  // no network round trip. Only populated on iOS; see hero-video-cache.ts.
  const cachedUri = getCachedHeroVideoUri(key);
  if (cachedUri) {
    return { uri: cachedUri };
  }
  // Not cached yet — play from the network. expo-video's built-in caching
  // stays disabled on iOS because it triggers a black-surface bug (see
  // expo/expo#43957); on Android it's a mild bonus on top of our own cache.
  return { uri: key, useCaching: Platform.OS !== 'ios' };
}

// Tuned so the first frame paints as fast as possible on cold plays. The hero
// is a muted background loop — a rare mid-playback stall is an acceptable
// tradeoff for consistently winning the splash-screen race against evergreen.
// - iOS `waitsToMinimizeStalling: false` tells AVPlayer to start rendering as
//   soon as it can decode a frame, instead of building a safety buffer first.
// - `preferredForwardBufferDuration: 1` keeps the forward buffer small.
// - Android `minBufferForPlayback: 0.5` lowers ExoPlayer's default 2s startup
//   buffer requirement.
const HERO_BUFFER_OPTIONS = {
  waitsToMinimizeStalling: false,
  preferredForwardBufferDuration: 1,
  minBufferForPlayback: 0.5,
};

// Creates a hero video player with the shared config. Used both for the
// bootstrap warm-up (`preloadHeroVideo`) and as the fallback when a layer
// mounts without a preloaded instance.
export function createHeroVideoPlayer(source: VideoSource): VideoPlayer {
  const player = createVideoPlayer(source);
  player.loop = true;
  player.muted = true;
  player.bufferOptions = HERO_BUFFER_OPTIONS;
  player.play();
  return player;
}

type Preloaded = {
  key: string;
  player: VideoPlayer;
};

// A single hero player warmed at app bootstrap so the first paint reads from a
// filled buffer (iOS — in memory) or the disk cache (Android) instead of a cold
// network fetch. The on-screen layer adopts it via `takePreloadedHeroVideo` and
// owns readiness/paint reporting from there (see hero-video-ready-signal).
let preloaded: Preloaded | null = null;

// once layer gets its playback release other preloads
let heroVideoLive = false;

// Ends the preload window and drops any warm player that no layer adopted
export function markHeroVideoLive(): void {
  heroVideoLive = true;
  if (preloaded) {
    preloaded.player.release();
    preloaded = null;
  }
}

// Whether a widget layer has adopted a hero video player. Callers use this to
// avoid touching files/state that a live player may hold open — deleting a
// cached file out from under an AVPlayer that's reading it can stall playback.
export function isHeroVideoLive(): boolean {
  return heroVideoLive;
}

// Resets the preload window. Only for tests, so module state doesn't leak
// between cases.
export function resetHeroPreloadStateForTests(): void {
  preloaded?.player.release();
  preloaded = null;
  heroVideoLive = false;
}

export function preloadHeroVideo(key: string, source: VideoSource): void {
  if (heroVideoLive) {
    // A layer already owns playback — warming now would only create an orphan.
    return;
  }
  if (preloaded?.key === key) {
    return; // already warming
  }
  // Source changed (e.g. an LTO toggled, or the CMS swapped the video) — drop
  // the stale one.
  preloaded?.player.release();
  preloaded = { key, player: createHeroVideoPlayer(source) };
}

export function takePreloadedHeroVideo(key: string): VideoPlayer | null {
  if (preloaded?.key === key) {
    const { player } = preloaded;
    preloaded = null;
    return player;
  }
  return null;
}
